import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../utils/api';
import recordActivity from '../utils/activity';
import { trackDashboardGenerateCta } from '../utils/analytics';
import safeStorage from '../utils/safeStorage';

// "Welcome back" recap, shown when a user returns after 3+ days away.
// For co-parents: what the family has been reading while they were gone.
// For everyone: an AI continuation hook that turns the last story into an
// ongoing arc — one tap generates the next chapter (no Series feature needed).
const VISIT_KEY = 'db_last_dashboard_visit';
const AWAY_DAYS = 3;

const StoryRecapCard = () => {
  const navigate = useNavigate();
  const [recap, setRecap] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const last = safeStorage.getItem(VISIT_KEY);
    const now = Date.now();
    // Always refresh the visit stamp so the next gap is measured from now
    safeStorage.setItem(VISIT_KEY, String(now));

    if (!last) return; // first ever visit — nothing to recap
    const daysAway = (now - parseInt(last, 10)) / 86400000;
    if (daysAway < AWAY_DAYS) return;

    API.get(`/stories/recap?since=${new Date(parseInt(last, 10)).toISOString()}`)
      .then(res => { if (res.data.away) setRecap(res.data); })
      .catch(() => {});
  }, []);

  if (!recap || dismissed) return null;

  const { isCoParent, newStories = [], continuation } = recap;
  const hasNews = newStories.length > 0;

  const continueStory = async () => {
    if (!continuation?.params || generating) return;
    setGenerating(true);
    try {
      const res = await API.post('/stories/generate', continuation.params);
      recordActivity('recap.continued', { childId: continuation.childId, from: continuation.lastStoryTitle }, continuation.childId);
      trackDashboardGenerateCta();
      if (res.data.pendingApproval) navigate('/dashboard/approval-queue?new=true');
      else navigate(`/dashboard/story/${res.data.storyId}?new=true&generating=true`);
    } catch {
      setGenerating(false);
    }
  };

  return (
    <div className="rounded-4 p-4 mb-4" style={{ background: 'linear-gradient(135deg,#2d1b69,#6f42c1)', color: '#fff', position: 'relative', overflow: 'hidden' }}>
      <button type="button" aria-label="Dismiss" onClick={() => setDismissed(true)}
        style={{ position: 'absolute', top: '12px', right: '14px', background: 'none', border: 'none', color: 'rgba(255,255,255,0.7)', fontSize: '18px', cursor: 'pointer', lineHeight: 1 }}>
        ✕
      </button>

      <div className="fw-bold mb-2" style={{ fontFamily: "'Fredoka One',cursive", fontSize: '18px' }}>
        👋 Welcome back!
      </div>

      {/* Co-parent / family recap of what was read while away */}
      {hasNews && (
        <div className="mb-3">
          <div style={{ color: '#c9a8ff', fontSize: '14px', marginBottom: '8px' }}>
            {isCoParent
              ? 'While you were away, here\'s what your family has been reading:'
              : 'Here\'s what\'s been read recently:'}
          </div>
          <div className="d-flex flex-column gap-1">
            {newStories.slice(0, 5).map((s, i) => (
              <div key={i} style={{ fontSize: '14px', color: '#fff' }}>
                📖 <strong>{s.title}</strong>
                {s.childName ? <span style={{ color: '#c9a8ff' }}> — for {s.childName}</span> : null}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Continuation hook — connect into an ongoing arc */}
      {continuation && (
        <div className="rounded-3 p-3" style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.18)' }}>
          <div style={{ fontSize: '14px', lineHeight: 1.6, marginBottom: '4px' }}>
            {continuation.hook}
          </div>
          <div style={{ color: '#c9a8ff', fontSize: '14px', lineHeight: 1.6, marginBottom: '12px' }}>
            {continuation.concept} <strong style={{ color: '#fff' }}>Tonight, continue the journey?</strong>
          </div>
          {continuation.params && (
            <button type="button" className="btn btn-warning rounded-pill px-4 fw-bold" onClick={continueStory} disabled={generating}>
              {generating
                ? <><span className="spinner-border spinner-border-sm me-1" style={{ width: '12px', height: '12px', borderWidth: '2px' }}></span>Continuing…</>
                : <>✨ Continue the Adventure</>}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default StoryRecapCard;
